import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MaskService } from 'react-native-masked-text';
import { Alert, Keyboard, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import ToolTip from 'design-system/ToolTip';
import { Button } from 'design-system/Button';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import { IconEdit } from 'design-system/icons/IconEdit';
import Text, { styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useFetchOld, { muteErrorAndResult } from 'hooks/useFetch';
import { capitalizeFirstCharacter, formatAmount, formatPlural } from 'utils/formatting';
import { useAppSelector } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { formatNumberOfSharesAmount } from '../utils';
import { strings } from '../components/AmountPreview';
import { getLimitStopOrderExpiryText } from '../utils/format';
import { useCurrentPrice, usePosition } from '../hooks/selectors';
import useLimitStopOrderConfirm from '../hooks/useLimitStopOrderConfirm';
import { LimitStopOrderExpiryDateType, SellOrderType } from '../types';
import { getOrderPreview } from '../actions';
import { selectActiveTradingAccountId } from '../reducers/selectors';
const maskedTextOptions = {
    unit: '',
    separator: '.',
    delimiter: ',',
    precision: 0,
};
const getErrorText = (numberOfShares, positionQty) => {
    if (numberOfShares.includes('.')) {
        return 'whole shares only';
    }
    const newShares = Number(numberOfShares.replace(/\D/g, ''));
    if (positionQty && newShares > positionQty) {
        return 'exceeds balance';
    }
    return undefined;
};
const LimitStopSellInfoScreen = ({ route, navigation }) => {
    const { symbol, amount, assetId } = route.params;
    const [, , fetching, fetch] = useFetchOld(muteErrorAndResult);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const position = usePosition(assetId);
    const textInputRef = useRef(null);
    const [shares, setShares] = useState('0');
    const [errorText, setErrorText] = useState(undefined);
    const [expiryDate, setExpiryDate] = useState({
        type: LimitStopOrderExpiryDateType.NINETY_DAYS,
    });
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const containerStyles = useMarginBottom('paddingBottom', undefined, styles.container);
    const [onConfirm] = useLimitStopOrderConfirm(symbol, tradingAccountId, assetId, 'sell');
    const currentPrice = useCurrentPrice(assetId);
    useAutoFocusInput(textInputRef);
    const { orderType } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const showErrorAlert = useCallback(() => {
        Alert.alert('Hold on 💫', 'You need to enter a valid amount.', [
            { onPress: () => textInputRef.current?.focus(), text: 'OK' },
        ]);
    }, []);
    const onSubmit = useCallback(async () => {
        const errorText = getErrorText(shares, position?.availableToSell);
        if (errorText) {
            setErrorText(errorText);
            showErrorAlert();
            return;
        }
        const sharesNum = Number(shares.replace(/\D/g, ''));
        if (!sharesNum) {
            showErrorAlert();
            return;
        }
        const limitStopOrderInfo = orderType === SellOrderType.LIMIT
            ? {
                orderType,
                limitPrice: amount.value,
            }
            : {
                orderType,
                stopPrice: amount.value,
            };
        const result = await fetch(getOrderPreview(sharesNum, assetId, undefined, 'sell', limitStopOrderInfo));
        if (result?.order) {
            onConfirm(amount, sharesNum, expiryDate, orderType, result.order.estimated.price, result.order.estimated.priceTimestamp, result.order.estimated.fxConversion, result.order.estimated.amount, result.order.estimated.priceConvertedToUserCurrency);
        }
    }, [amount, assetId, expiryDate, onConfirm, orderType, position?.availableToSell, shares, showErrorAlert]);
    const onChangeText = useCallback((newText) => {
        const newShares = Number(newText.replace(/\D/g, ''));
        setShares(MaskService.toMask('money', String(newShares), maskedTextOptions));
        setErrorText(getErrorText(newText, position?.availableToSell));
    }, [position]);
    const onSellAll = useCallback(() => {
        if (position) {
            setShares(String(Math.floor(position.availableToSell)));
        }
    }, [position]);
    const onSelectExpiryDate = useCallback((expiryDate) => {
        setExpiryDate(expiryDate);
    }, []);
    const handleChangeExpiryDate = useCallback(() => {
        Keyboard.dismiss();
        navigation.navigateDeprecated('InvestLimitStopOrderSetExpiryDate', { onSelectExpiryDate });
    }, [onSelectExpiryDate]);
    return (<ScrollView scrollEnabled={false} keyboardShouldPersistTaps="handled" contentContainerStyle={styles.contentContainerStyles}>
      <AppView withFrame avoidHeader>
        <View style={containerStyles}>
          <View style={styles.flex}>
            <View style={styles.headerContainer}>
              <Text Text-24>Sell ${symbol}</Text>
            </View>

            <CardView noMarginBottom>
              <View style={styles.paddedContainer}>
                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text Text-16>Amount of shares</Text>
                    {!!position && (<Text TextThin-14 Secondary>
                        <Text NumbersThin-14 Secondary>
                          {formatNumberOfSharesAmount(position.availableToSell)}
                        </Text>{' '}
                        {formatPlural('share', 'shares', position.availableToSell)} available
                      </Text>)}
                  </View>
                  <View style={styles.rightContainer}>
                    <TextInput value={shares} ref={textInputRef} returnKeyType="done" keyboardType="numeric" style={styles.textInput} onChangeText={onChangeText} underlineColorAndroid="transparent" selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder}/>
                    <Text TextThin-14 Red>
                      {errorText || ' '}
                    </Text>
                  </View>
                </View>
                <Spacer h={16}/>
                <Button square small brandReversed title="Sell all" onPress={onSellAll}/>
              </View>
              <View style={styles.line}/>

              <View style={styles.paddedContainer}>
                <View style={styles.row}>
                  <View style={styles.flex}>
                    <View style={styles.row}>
                      <Text Text-16>{strings.title}</Text>
                      <Spacer w={4}/>
                      <ToolTip small offset={1} colorKey="secondary" title={strings.tooltipTitle} subtitle={strings.tooltipText}/>
                    </View>
                    {!!currentPrice && (<>
                        <Spacer h={4}/>
                        <Text NumbersThin-14 Secondary>
                          <Text TextThin-14 Secondary>
                            {strings.latestPrice}
                          </Text>
                          {formatAmount(currentPrice.value, currentPrice.currency, 2)}
                        </Text>
                      </>)}
                  </View>
                  <Text Numbers-16>
                    {formatAmount(amount.value * Number(shares.replace(/\D/g, '')), amount.currency, 2)}
                  </Text>
                </View>
              </View>
            </CardView>

            <CardView withPadding>
              <View style={styles.row}>
                <View style={styles.flex}>
                  <Text TextThin-14 Secondary>
                    {capitalizeFirstCharacter(orderType)} price
                  </Text>
                </View>
                <Text Numbers-14>{formatAmount(amount.value, amount.currency, 2)}</Text>
              </View>
              <Spacer h={10}/>
              <View style={styles.row}>
                <View style={styles.flex}>
                  <Text TextThin-14 Secondary>
                    Expiry
                  </Text>
                </View>
                <TouchableOpacity style={styles.expiryBtn} onPress={handleChangeExpiryDate}>
                  <IconEdit width={16} height={16} colorKey="brand"/>
                  <Spacer w={4}/>
                  <Text Text-14>{getLimitStopOrderExpiryText(expiryDate)}</Text>
                </TouchableOpacity>
              </View>
            </CardView>
          </View>
          <View style={styles.buttonContainer}>
            <Button loading={fetching} flex brand title="Review order" onPress={onSubmit}/>
          </View>
        </View>
      </AppView>
    </ScrollView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    flex: {
        flex: 1,
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(16),
    },
    headerContainer: {
        paddingHorizontal: rem(16),
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    paddedContainer: {
        padding: rem(16),
    },
    line: {
        height: rem(1),
        backgroundColor: colors.background.dark,
    },
    textInput: {
        ...textStyles.Numbers_20,
        color: colors.text.primary,
    },
    contentContainerStyles: {
        flexGrow: 1,
    },
    expiryBtn: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    rightContainer: {
        alignItems: 'flex-end',
    },
}));
export default LimitStopSellInfoScreen;
